import { CASES } from "./constants";
import { safeGetJSON } from "tap/utils/safeLocalStorage";
import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  getInitialPaginationRange,
  getStoredItemsPerPage,
  PAGINATION_STORAGE_KEYS,
  persistItemsPerPageFromPayload,
  withPreservedPagination,
} from "tap/utils/paginationPreferences.js";

const paginationKeys = {
  itemsPerPage: PAGINATION_STORAGE_KEYS.tapJobs,
  jobViewItemsPerPage: PAGINATION_STORAGE_KEYS.tapJobsJobView,
  jobTasksItemsPerPage: PAGINATION_STORAGE_KEYS.tapJobsTasks,
};
const initialItemsPerPage = getStoredItemsPerPage(paginationKeys.itemsPerPage);
const initialJobViewItemsPerPage = getStoredItemsPerPage(
  paginationKeys.jobViewItemsPerPage,
);
const initialJobTasksItemsPerPage = getStoredItemsPerPage(
  paginationKeys.jobTasksItemsPerPage,
);
const initialPaginationRange = getInitialPaginationRange(initialItemsPerPage);

/**
 * Retrieves persisted sort preference from localStorage.
 * @returns {Array} Sort tuple [field, direction], defaults to ['id', 'DESC']
 */
const getInitialJobsSort = () => {
  return safeGetJSON("tapJobSort", ["id", "DESC"]);
};

/**
 * Initial state for the job tasks modal (separate from job view modal).
 * Separated for use with CLEAR_JOB_TASKS_MODAL action.
 */
const initialJobTaskState = {
  jobTasks: [],
  taskFilterForm: {
    jobId: "",
    taskStatus: "",
  },
  isTasksModalOpen: false,
  jobTasksTotalPages: 0,
  jobTasksTotalItems: 0,
  jobTasksCurrentPage: 0,
  jobTasksRangeParam: initialPaginationRange,
  jobTasksDefaultFilterRange: initialPaginationRange,
  jobTasksSortParam: ["id", "DESC"],
  jobTasksItemsPerPage: initialJobTasksItemsPerPage,
};

/**
 * Initial state for the job view modal (task list within a job).
 * Separated for use with CLEAR_JOB_VIEW_MODAL action.
 */
const initialJobViewState = {
  jobViewTasks: [],
  jobViewFilterForm: {
    jobId: "",
    jobStatus: "",
    dealsProcessed: "",
    taskStatus: {
      value: "",
      label: "All",
    },
  },
  isJobViewModalOpen: false,
  taskStatusOptions: [
    { value: "", label: "All" },
    { value: "COMPLETE", label: "Successes" },
    { value: "INVALID", label: "Failures" },
  ],
  /** Job statuses that indicate the job is still processing and may need polling */
  pendingStatuses: [
    "RUNNING",
    "CREATED",
    "STARTING",
    "RETRYING",
    "QUEUED",
    "QUEUED_ADMINISTRATOR",
    "QUEUED_UPDATED",
    "QUEUED_UPDATED_USER_INITIATED",
    "QUEUED_ADMINISTRATOR",
    "QUEUED_UPDATED_ADMINISTRATOR",
  ],
  jobViewTotalPages: 0,
  jobViewCurrentPage: 0,
  jobViewTotalItems: 0,
  jobViewItemsPerPage: initialJobViewItemsPerPage,
};

/**
 * TapJobs Redux State Shape
 * @typedef {Object} TapJobsState
 * @property {Array} jobs - List of TAP job records from API
 * @property {boolean} refreshJobs - Flag to trigger grid data refetch
 * @property {Array} jobStatuses - Available job status values
 * @property {Object} filterForm - Search/filter form values (jobId, jobType, jobStatus)
 * @property {string} selectedJob - Currently selected job ID for viewing
 * @property {boolean} isJobViewModalOpen - Job view modal visibility
 * @property {Array} jobStatusOptions - Job status dropdown options (from API)
 * @property {Array} jobTypeOptions - Job type dropdown options (from API)
 * @property {number} totalPages - Pagination total pages
 * @property {number} totalItems - Total record count
 * @property {Array} tapJobRange - Pagination range [start, end]
 * @property {Array} tapJobSort - Sort tuple [field, direction]
 */
const initialState = {
  jobs: [],
  refreshJobs: false,
  jobStatuses: [],
  filterForm: {
    jobId: "",
    jobType: "",
    jobStatus: "",
  },
  selectedJob: "",
  isJobViewModalOpen: false,
  jobStatusOptions: [],
  jobTypeOptions: [],
  totalPages: 0,
  totalItems: 0,
  currentPage: 0,
  filterParam: "",
  tapJobRange: initialPaginationRange,
  tapJobSort: getInitialJobsSort(),
  defaultFilterRange: initialPaginationRange,
  itemsPerPage: initialItemsPerPage,
  pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
  ...initialJobViewState,
  ...initialJobTaskState,
};

export function tapJobs(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case CASES.UPDATE_JOBS: {
      // Persist sort preference to localStorage for session continuity
      if (payload.tapJobSort) {
        localStorage.setItem("tapJobSort", JSON.stringify(payload.tapJobSort));
      }
      persistItemsPerPageFromPayload(payload, paginationKeys);
      return { ...state, ...payload };
    }
    case CASES.UPDATE_JOBS_SEARCH_FORM: {
      const { data, key } = payload;
      return { ...state, [key]: { ...state[key], ...data } };
    }
    case CASES.CLEAR_JOBS: {
      return withPreservedPagination(
        state,
        {
          ...initialState,
          jobStatusOptions: state.jobStatusOptions,
          jobTypeOptions: state.jobTypeOptions,
          tapJobSort: state.tapJobSort,
        },
        ["tapJobRange"],
        ["itemsPerPage", "jobViewItemsPerPage", "jobTasksItemsPerPage"],
      );
    }
    case CASES.CLEAR_JOB_VIEW_MODAL: {
      return {
        ...state,
        ...initialJobViewState,
        jobViewItemsPerPage: state.jobViewItemsPerPage,
        selectedJob: "",
      };
    }
    case CASES.CLEAR_JOB_TASKS_MODAL: {
      return {
        ...state,
        ...initialJobTaskState,
        jobTasksItemsPerPage: state.jobTasksItemsPerPage,
        selectedJob: "",
      };
    }
    default:
      return state;
  }
}
